import { useI18n } from '@ecom/i18n';
import { Drawer, EmptyState } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useCustomerCouponUsagesQuery } from '../api';
import type { CustomerSummary } from '../types';

import { OrderStatusBadge } from '@/features/orders/components/OrderStatusBadge';
import { formatCatalogPrice } from '@/features/products/format-price';

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('fr-TN', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

interface PurchasedProduct {
  readonly sku: string;
  readonly productName: string;
  readonly quantity: number;
  readonly total: number;
}

function purchasedProducts(customer: CustomerSummary): readonly PurchasedProduct[] {
  const bySku = new Map<string, PurchasedProduct>();
  for (const order of customer.orders) {
    if (order.status === 'CANCELLED') continue;
    for (const item of order.items) {
      const existing = bySku.get(item.sku);
      if (existing !== undefined) {
        bySku.set(item.sku, {
          ...existing,
          quantity: existing.quantity + item.quantity,
          total: existing.total + item.lineTotal,
        });
      } else {
        bySku.set(item.sku, {
          sku: item.sku,
          productName: item.productName,
          quantity: item.quantity,
          total: item.lineTotal,
        });
      }
    }
  }
  return Array.from(bySku.values()).sort((a, b) => b.quantity - a.quantity);
}

/**
 * "Vue détail" — historique commandes, produits achetés, coupons
 * utilisés, tous les trois recalculés depuis les vraies commandes de ce
 * client (déjà chargées par la liste) sauf les coupons, qui exigent un
 * appel réel par coupon (`useCustomerCouponUsagesQuery`).
 */
export function CustomerDetailDrawer({
  customer,
  onClose,
}: {
  customer: CustomerSummary;
  onClose: () => void;
}): ReactElement {
  const { t } = useI18n();
  const couponUsages = useCustomerCouponUsagesQuery(customer.phone);
  const products = purchasedProducts(customer);

  return (
    <Drawer open title={customer.name} onClose={onClose}>
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-3 gap-3 rounded-lg border border-line bg-ground p-3 text-center">
          <div>
            <p className="font-mono text-lg font-bold text-ink">{customer.orderCount}</p>
            <p className="text-xs text-inkFaint">{t('merchant.clients.orderCountLabel')}</p>
          </div>
          <div>
            <p className="font-mono text-lg font-bold text-ink">
              {formatCatalogPrice(customer.totalSpent)}
            </p>
            <p className="text-xs text-inkFaint">{t('merchant.clients.table.colTotalSpent')}</p>
          </div>
          <div>
            <p className="font-mono text-xs font-semibold text-ink">
              {formatDateTime(customer.lastActivityAt)}
            </p>
            <p className="text-xs text-inkFaint">{t('merchant.clients.table.colLastActivity')}</p>
          </div>
        </div>

        <section>
          <h3 className="mb-2 font-display text-sm font-bold text-ink">
            {t('merchant.clients.orderHistoryTitle')}
          </h3>
          <ul className="flex flex-col gap-2">
            {customer.orders.map((order) => (
              <li
                key={order.id}
                className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm"
              >
                <div>
                  <span className="font-mono font-semibold text-ink">#{order.orderNumber}</span>
                  <p className="text-xs text-inkFaint">{formatDateTime(order.createdAt)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <OrderStatusBadge status={order.status} />
                  <span className="font-mono text-ink">{formatCatalogPrice(order.grandTotal)}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h3 className="mb-2 font-display text-sm font-bold text-ink">
            {t('merchant.clients.purchasedProductsTitle')}
          </h3>
          {products.length === 0 ? (
            <p className="text-sm text-inkFaint">{t('merchant.clients.noNonCancelledProducts')}</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {products.map((product) => (
                <li
                  key={product.sku}
                  className="flex items-center justify-between text-sm text-inkSoft"
                >
                  <span>
                    {product.quantity} × {product.productName}
                  </span>
                  <span className="font-mono text-ink">{formatCatalogPrice(product.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h3 className="mb-2 font-display text-sm font-bold text-ink">
            {t('merchant.clients.couponsUsedTitle')}
          </h3>
          {couponUsages.isPending ? (
            <p className="text-sm text-inkFaint">{t('merchant.common.loadingAria')}…</p>
          ) : couponUsages.usages.length === 0 ? (
            <EmptyState
              title={t('merchant.clients.noCouponUsed')}
              description={t('merchant.clients.noCouponUsedDescription')}
            />
          ) : (
            <ul className="flex flex-col gap-1.5">
              {couponUsages.usages.map((usage, index) => (
                <li key={index} className="flex items-center justify-between text-sm">
                  <span className="font-mono font-medium text-ink">{usage.couponCode}</span>
                  <span className="text-inkFaint">
                    -{formatCatalogPrice(usage.discountAmount)} {t('merchant.clients.couponOn')} #
                    {usage.orderNumber}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Drawer>
  );
}
